import React, { useContext, useEffect, useCallback, useMemo } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Grid,
  Button,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl
} from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import { useForm, Controller } from 'react-hook-form';
import esLocale from 'date-fns/locale/es';
import DateFnsUtils from '@date-io/date-fns';
import { useStateValue } from 'context/store';
import { listTypePqr, listSubjectPqr } from 'context/claims/actions';
import { PQR_STATUSES } from 'utils/constants';
import { formatDate, cleanObject } from 'utils';
import { I18nContext } from 'translations';
import styles from '../../styleTableFilters';

const SUBJECT_TYPE_FILTERS = {
  1: { allowedCds: [1, 3, 4, 5, 6] }, // Quejas
  2: { allowedCds: [0, 2] }            // Reclamaciones
};

const defaultValues = {
  ini_date: null,
  end_date: null,
  booking_id: '',
  ticket_id: '',
  status_cd: '',
  subject_cd: '',
  type_of_request_cd: ''
};

const ClaimsTableFilters = ({ classes, onFilters }) => {
  const { translate } = useContext(I18nContext);
  const { handleSubmit, control, reset, watch, setValue } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const selectedSubject = watch('subject_cd');
  const selectedType = watch('type_of_request_cd');

  const [{ claims: { typePqr, subjectPqr } }, dispatch] = useStateValue();

  const getTypePQR = useCallback(async () => {
    await listTypePqr(dispatch);
  }, [dispatch]);

  const getSubjectPQR = useCallback(async () => {
    await listSubjectPqr(dispatch);
  }, [dispatch]);

  useEffect(() => {
    getTypePQR();
    getSubjectPQR();
  }, [getTypePQR, getSubjectPQR]);

  const filteredTypePqr = useMemo(() => {
    if (!typePqr) return [];
    if (!selectedSubject && selectedSubject !== 0) return typePqr.filter(t => t.cd !== 7);

    if (selectedSubject === 0 || selectedSubject === 3 || selectedSubject === 4) {
      return typePqr.filter(t => t.cd === 7);
    }

    const subjectFilter = SUBJECT_TYPE_FILTERS[selectedSubject];
    if (subjectFilter) {
      return typePqr.filter(t => subjectFilter.allowedCds.includes(t.cd));
    }

    return typePqr.filter(t => t.cd !== 7);
  }, [typePqr, selectedSubject]);

  useEffect(() => {
    if (selectedSubject === '' || selectedSubject === undefined) {
      setValue('type_of_request_cd', '');
      return;
    }
    if (!filteredTypePqr?.length) return;
    setValue('type_of_request_cd', filteredTypePqr[0].cd);
  }, [selectedSubject, filteredTypePqr, setValue]);

  useEffect(() => {
    if (selectedType === '' || selectedType === undefined) {
      setValue('subject_cd', '');
    }
  }, [selectedType, setValue]);

  const onSubmit = (values) => {
    let params = {
      ...values,
      ini_date: formatDate(values?.ini_date),
      end_date: formatDate(values?.end_date),
    };
    params = cleanObject(params);
    onFilters(params);
  };

  const clearFilters = () => {
    reset(defaultValues);
    onFilters('');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate className="relative">
      <Grid container spacing={2} alignItems="center">
        <Grid item md={3} sm={4} xs={6}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
            <Controller
              name="ini_date"
              control={control}
              defaultValue={null}
              render={({ onChange, value }) => (
                <KeyboardDatePicker
                  className={classes.filterStyle}
                  inputVariant="outlined"
                  label={translate('claims.table.filter.dateFrom')}
                  format="dd/MM/yyyy"
                  cancelLabel={translate('cancel')}
                  okLabel={translate('accept')}
                  invalidDateMessage={translate('invalidFormat')}
                  fullWidth
                  value={value}
                  onChange={onChange}
                  size="small"
                />
              )}
            />
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
            <Controller
              name="end_date"
              control={control}
              defaultValue={null}
              render={({ onChange, value }) => (
                <KeyboardDatePicker
                  className={classes.filterStyle}
                  inputVariant="outlined"
                  label={translate('claims.table.filter.dateUntil')}
                  format="dd/MM/yyyy"
                  cancelLabel={translate('cancel')}
                  okLabel={translate('accept')}
                  invalidDateMessage={translate('invalidFormat')}
                  fullWidth
                  value={value}
                  onChange={onChange}
                  size="small"
                />
              )}
            />
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <FormControl
            variant="outlined"
            className={classes.filterStyle}
            fullWidth
            size="small"
          >
            <InputLabel>{translate('claims.form.typeOfPqr')}</InputLabel>
            <Controller
              control={control}
              name="subject_cd"
              label={translate('claims.form.typeOfPqr')}
              variant="outlined"
              as={
                <Select>
                  <MenuItem value="">{translate('claims.form.typeOfPqr')}</MenuItem>
                  {subjectPqr?.map((type) => (
                    <MenuItem value={type.cd} key={`subject-pqr-${type.cd}`}>
                      {type.text}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
          </FormControl>
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <FormControl
            variant="outlined"
            className={classes.filterStyle}
            fullWidth
            size="small"
          >
            <InputLabel>{translate('claims.table.subject')}</InputLabel>
            <Controller
              control={control}
              name="type_of_request_cd"
              label={translate('claims.table.subject')}
              variant="outlined"
              as={
                <Select>
                  <MenuItem value="">{translate('claims.table.subject')}</MenuItem>
                  {filteredTypePqr?.map((type) => (
                    <MenuItem value={type.cd} key={`type-pqr-${type.cd}`}>
                      {type.text}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
          </FormControl>
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <FormControl
            variant="outlined"
            className={classes.filterStyle}
            fullWidth
            size="small"
          >
            <InputLabel>{translate('claims.table.filter.claimStatus')}</InputLabel>
            <Controller
              control={control}
              name="status_cd"
              label={translate('claims.table.filter.claimStatus')}
              variant="outlined"
              as={
                <Select>
                  <MenuItem value="">{translate('claims.table.filter.claimStatus')}</MenuItem>
                  {Object.keys(PQR_STATUSES).map(key => (
                    <MenuItem key={key} value={key}>{translate(PQR_STATUSES[key])}</MenuItem>
                  ))}
                </Select>
              }
            />
          </FormControl>
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <Controller
            name="booking_id"
            control={control}
            variant="outlined"
            defaultValue=""
            render={({ onChange, value }) => (
              <TextField
                size="small"
                className={classes.filterStyle}
                variant="outlined"
                label={translate('claims.table.filter.bookingId')}
                fullWidth
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <Controller
            name="ticket_id"
            control={control}
            variant="outlined"
            defaultValue=""
            render={({ onChange, value }) => (
              <TextField
                size="small"
                className={classes.filterStyle}
                variant="outlined"
                label={translate('claims.table.filter.ticketId')}
                fullWidth
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Grid>
        <Grid item md={6} xs={8} className="text-center-sm">
          <Button
            variant="outlined"
            color="primary"
            type="button"
            style={{ width: 120, marginRight: 10 }}
            onClick={clearFilters}
            className="btn-rounded-style"
          >
            {translate('clear')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            style={{ width: 120 }}
            className="btn-rounded-style"
          >
            {translate('search')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default withStyles(styles)(ClaimsTableFilters);
